import { DateTime } from 'luxon';
import { getCurrentOrgID, getOrganization, getOrgTimezone } from './organization';
import { reportRangeBounds, toReportDay } from '../utils/reportDates';
import ErrorHandler from '../utils/ErrorHandler';
import { supabase, queryClient } from './client';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { insertActivity } from './activity';

/**
 * Bible Studies data layer — thin wrappers around the settled P0/P1 backend
 * contract (see BIBLE-STUDIES-P0-CONTRACT.md at the workspace root).
 *
 * - Lists/sessions/reporting go through the dedicated RPCs. Do NOT reuse
 *   smallGroups.js session writes: they blanket-reactivate non-active groups
 *   and write legacy EventInstances.notes (unprotected).
 * - Protected session notes: direct PostgREST on BibleStudySessionNotes (RLS).
 * - Student notes: PeopleNotes rows with visibility='BIBLE_STUDY'.
 * - Organizations.enableBibleStudies is a UI flag ONLY — it never feeds
 *   backend authorization. 'all' scope still requires Manage Bible Studies.
 */

// ── Flag ────────────────────────────────────────────────────────────
// Reads through the shared organization query cache (same key components use),
// so a mid-session flag flip invalidates here too. Org switch clears the
// query client entirely, which resets this naturally.
const isBibleStudiesEnabled = async () => {
    const orgID = await getCurrentOrgID();
    if (!orgID) return false;

    const org = await queryClient.fetchQuery({
        queryKey: QueryKeys.organization.byId(orgID),
        queryFn: () => getOrganization(orgID),
        staleTime: 1000 * 60 * 15,
    });

    return org?.enableBibleStudies === true;
};

// ── Error mapping ───────────────────────────────────────────────────
// Backend raises: 42501 BIBLE_STUDY_DENIED / BIBLE_STUDY_NOT_FOUND,
// 22023 BIBLE_STUDY_INVALID, 40001 BIBLE_STUDY_CONFLICT.
const bibleStudyErrorMessage = (error, fallback) => {
    const message = error?.message || '';
    if (message.includes('BIBLE_STUDY_DENIED')) {
        return "You don't have access to this Bible Study. Contact your administrator if you think this is incorrect.";
    }
    if (message.includes('BIBLE_STUDY_NOT_FOUND')) {
        return 'That Bible Study could not be found. It may have been removed.';
    }
    if (message.includes('BIBLE_STUDY_INVALID') || error?.code === '22023') {
        return 'Some of the details were invalid. Check the teacher, attendees, and dates, then try again.';
    }
    if (message.includes('BIBLE_STUDY_CONFLICT') || error?.code === '40001') {
        return 'This conflicts with another record. Refresh and try again.';
    }
    return fallback;
};

const bibleStudyError = (error, context, fallback) => {
    ErrorHandler(Error(error?.message || 'Bible Study error'), { ...context, error }, bibleStudyErrorMessage(error, fallback));
};

// ── List / detail reads ─────────────────────────────────────────────
const PAGE_SIZE = 25;
const SESSION_PAGE_SIZE = 50;

const getBibleStudies = async ({ scope = 'own', status = null, searchTerm = null, teacherID = null, pageParam = 0, sortKey = null, sortDirection = null }) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase.rpc('get_bible_studies', {
        p_org_id: orgID,
        p_scope: scope,
        p_status: status || null,
        p_search: searchTerm || null,
        p_teacher_id: teacherID || null,
        p_offset: pageParam * PAGE_SIZE,
        p_limit: PAGE_SIZE,
        p_sort_key: sortKey || null,
        p_sort_direction: sortDirection || null,
    });

    if (error) {
        bibleStudyError(error, { query: 'getBibleStudies', input: { organizationID: orgID, scope, status, searchTerm, teacherID } }, 'Something went wrong fetching the Bible Studies');
        return { nextPage: undefined, data: [] };
    }

    return { nextPage: data.length < PAGE_SIZE ? undefined : pageParam + 1, data };
};

const getBibleStudySessions = async ({ scope = 'own', smallGroupID = null, startDate = null, endDate = null, teacherID = null, searchTerm = null, pageParam = 0, sortKey = null, sortDirection = null }) => {
    const { start, end } = await reportRangeBounds(startDate, endDate);

    const { error, data } = await supabase.rpc('get_bible_study_sessions', {
        p_scope: scope,
        p_small_group_id: smallGroupID || null,
        p_start_date: start,
        p_end_date: end,
        p_teacher_id: teacherID || null,
        p_search_term: searchTerm || null,
        p_page_offset: pageParam * SESSION_PAGE_SIZE,
        p_page_limit: SESSION_PAGE_SIZE,
        p_sort_key: sortKey || null,
        p_sort_direction: sortDirection || null,
    });

    if (error) {
        bibleStudyError(error, { query: 'getBibleStudySessions', input: { scope, smallGroupID, teacherID, searchTerm } }, 'Something went wrong fetching sessions');
        return { data: [], nextPage: undefined };
    }

    const sessionIDs = (data || []).filter(row => row.has_note).map(row => row.session_id);
    let notes = [];
    if (sessionIDs.length) {
        const result = await supabase.rpc('get_bible_study_session_note_details', { p_session_ids: sessionIDs });
        if (result.error) {
            bibleStudyError(result.error, { query: 'getBibleStudySessions.notes' }, 'Something went wrong loading session notes');
            throw Error('Unable to load session notes');
        }
        notes = result.data || [];
    }
    const notesBySession = new Map(notes.map(note => [note.eventInstanceID, note]));

    return {
        // Never fall back to legacy_notes: the note-detail RPC enforces the
        // protected reader set even when the caller can list the session.
        data: (data || []).map(row => ({ ...row, sessionNote: notesBySession.get(row.session_id) || null })),
        nextPage: data?.length === SESSION_PAGE_SIZE ? pageParam + 1 : undefined,
    };
};

const getBibleStudyStudents = async (smallGroupID) => {
    const { data, error } = await supabase.rpc('get_bible_study_students', { p_small_group_id: smallGroupID });
    if (error) {
        bibleStudyError(error, { query: 'getBibleStudyStudents', input: { smallGroupID } }, 'Something went wrong loading students');
        throw Error('Unable to load students');
    }
    return data || [];
};

// ── Session report (atomic, server-side) ────────────────────────────
// p_attendee_ids: NULL → all current members, [] → none.
// p_people_notes: [{ personID, content }] → linked PeopleNotes rows.
const reportBibleStudySession = async ({ smallGroupID, startTime, endTime = null, teacherID = null, attendeeIDs = null, sessionNote = null, peopleNotes = null, customFieldValues = null }) => {
    const { error, data } = await supabase.rpc('report_bible_study_session', {
        p_small_group_id: smallGroupID,
        p_start_time: startTime,
        p_end_time: endTime || null,
        p_teacher_id: teacherID || null,
        p_attendee_ids: attendeeIDs ?? null,
        p_session_note: sessionNote || null,
        p_people_notes: peopleNotes?.length ? peopleNotes : null,
        p_custom_field_values: customFieldValues || null,
    });

    if (error) {
        bibleStudyError(error, { mutation: 'reportBibleStudySession', input: { smallGroupID, teacherID } }, 'Something went wrong saving the session');
        throw Error();
    }

    insertActivity({ subjectType: 'BibleStudySession', subjectID: data?.smallGroupID || smallGroupID, subjectName: 'Bible Study Session', verb: 'create' }).catch(() => {});

    return data;
};

// ── Lifecycle ───────────────────────────────────────────────────────
const closeBibleStudy = async ({ smallGroupID, outcome }) => {
    const { error, data } = await supabase.rpc('close_bible_study', {
        p_small_group_id: smallGroupID,
        p_outcome: outcome, // 'COMPLETED' | 'CANCELED'
    });

    if (error) {
        bibleStudyError(error, { mutation: 'closeBibleStudy', input: { smallGroupID, outcome } }, `Something went wrong ${outcome === 'COMPLETED' ? 'completing' : 'canceling'} the study`);
        throw Error();
    }

    insertActivity({ subjectType: 'BibleStudy', subjectID: smallGroupID, subjectName: data?.name, verb: outcome === 'COMPLETED' ? 'complete' : 'cancel' }).catch(() => {});

    return data;
};

const reopenBibleStudy = async ({ smallGroupID }) => {
    const { error, data } = await supabase.rpc('reopen_bible_study', {
        p_small_group_id: smallGroupID,
    });

    if (error) {
        bibleStudyError(error, { mutation: 'reopenBibleStudy', input: { smallGroupID } }, 'Something went wrong reopening the study');
        throw Error();
    }

    insertActivity({ subjectType: 'BibleStudy', subjectID: smallGroupID, subjectName: data?.name, verb: 'reopen' }).catch(() => {});

    return data;
};

// ── Protected session notes (BibleStudySessionNotes, RLS-enforced) ──
const SESSION_NOTE_SELECT = `
    id,
    organizationID,
    smallGroupID,
    eventInstanceID,
    content,
    createdAt,
    updatedAt,
    createdBy,
    Author:People!createdBy(id, firstName, lastName, avatarStorageKey, avatarUpdatedAt),
    EventInstance:EventInstances!eventInstanceID(startTime)
`;

const getBibleStudySessionNotes = async (smallGroupID) => {
    const { error, data } = await supabase
        .from('BibleStudySessionNotes')
        .select(SESSION_NOTE_SELECT)
        .eq('smallGroupID', smallGroupID)
        .order('createdAt', { ascending: false });

    if (error) {
        // RLS denial surfaces as empty/error — keep it quiet, the caller shows
        // an honest empty state rather than a permission toast on read.
        if (!error.message?.includes('row-level security')) {
            bibleStudyError(error, { query: 'getBibleStudySessionNotes', input: { smallGroupID } }, 'Something went wrong loading session notes');
        }
        return [];
    }

    return data || [];
};

const updateBibleStudySessionNote = async ({ id, content }) => {
    const { error, data } = await supabase
        .from('BibleStudySessionNotes')
        .update({ content })
        .eq('id', id)
        .select(SESSION_NOTE_SELECT)
        .single();

    if (error) {
        bibleStudyError(error, { mutation: 'updateBibleStudySessionNote', input: { id } }, 'Something went wrong updating the note');
        throw Error();
    }

    return data;
};

const deleteBibleStudySessionNote = async (id) => {
    const { error } = await supabase
        .from('BibleStudySessionNotes')
        .delete()
        .eq('id', id);

    if (error) {
        bibleStudyError(error, { mutation: 'deleteBibleStudySessionNote', input: { id } }, 'Something went wrong deleting the note');
        throw Error();
    }
};

// ── Teacher detail report ───────────────────────────────────────────
// p_start_date/p_end_date are DATE params (org-local 'yyyy-MM-dd'), NOT
// instants — different from the timestamptz session RPCs. Default range
// is the trailing 90 org-local days when the caller passes no dates.
const TEACHER_DETAIL_DEFAULT_DAYS = 90;

const bibleStudyTeacherDetail = async ({ scope = 'own', teacherID, startDate = null, endDate = null }) => {
    let start = await toReportDay(startDate);
    let end = await toReportDay(endDate);
    if (!start || !end) {
        const tz = await getOrgTimezone();
        const now = DateTime.now().setZone(tz);
        if (!end) end = now.toISODate();
        if (!start) start = now.minus({ days: TEACHER_DETAIL_DEFAULT_DAYS }).toISODate();
    }

    const { error, data } = await supabase.rpc('bible_study_report_teacher_detail', {
        p_scope: scope,
        p_teacher_id: teacherID || null,
        p_start_date: start,
        p_end_date: end,
    });

    if (error) {
        bibleStudyError(error, { query: 'bibleStudyTeacherDetail', input: { scope, teacherID, start, end } }, 'Something went wrong loading teacher details');
        return null;
    }

    return data;
};

// ── Student outcomes report ─────────────────────────────────────────
// bible_study_report_student_outcomes: per-teacher student totals over an
// org-local trailing window (contributions sum gated server-side by View
// Contributions — totalContributions comes back null when not permitted).
const bibleStudyStudentOutcomes = async ({ scope = 'own', teacherID, startDate = null, endDate = null, windowDays = TEACHER_DETAIL_DEFAULT_DAYS }) => {
    let start = await toReportDay(startDate);
    let end = await toReportDay(endDate);
    if (!start || !end) {
        const tz = await getOrgTimezone();
        const now = DateTime.now().setZone(tz);
        if (!end) end = now.toISODate();
        if (!start) start = now.minus({ days: windowDays }).toISODate();
    }

    const { error, data } = await supabase.rpc('bible_study_report_student_outcomes', {
        p_scope: scope,
        p_teacher_id: teacherID || null,
        p_start_date: start,
        p_end_date: end,
    });

    if (error) {
        bibleStudyError(error, { query: 'bibleStudyStudentOutcomes', input: { scope, teacherID, start, end } }, 'Something went wrong loading student outcomes');
        return null;
    }

    return data;
};

// ── Student notes (PeopleNotes, visibility='BIBLE_STUDY') ───────────
// One note lives once here — linked to person + study + session. The same
// row is read/edited from the study surface and the People profile notes tab.
const getBibleStudyPeopleNotes = async (smallGroupID) => {
    const { error, data } = await supabase
        .from('PeopleNotes')
        .select(`
            id,
            personID,
            smallGroupID,
            eventInstanceID,
            content,
            visibility,
            createdAt,
            updatedAt,
            createdBy,
            Author:People!createdBy(id, firstName, lastName, avatarStorageKey, avatarUpdatedAt),
            EventInstance:EventInstances!eventInstanceID(startTime),
            Person:People!personID(id, firstName, lastName, avatarStorageKey, avatarUpdatedAt)
        `)
        .eq('smallGroupID', smallGroupID)
        .eq('visibility', 'BIBLE_STUDY')
        .order('createdAt', { ascending: false });

    if (error) {
        if (!error.message?.includes('row-level security')) {
            bibleStudyError(error, { query: 'getBibleStudyPeopleNotes', input: { smallGroupID } }, 'Something went wrong loading student notes');
        }
        throw Error('Unable to load student notes');
    }

    return data || [];
};

export {
    isBibleStudiesEnabled,
    bibleStudyErrorMessage,
    getBibleStudies,
    getBibleStudySessions,
    getBibleStudyStudents,
    bibleStudyTeacherDetail,
    bibleStudyStudentOutcomes,
    reportBibleStudySession,
    closeBibleStudy,
    reopenBibleStudy,
    getBibleStudySessionNotes,
    updateBibleStudySessionNote,
    deleteBibleStudySessionNote,
    getBibleStudyPeopleNotes,
};
